import { techniqueById } from './data';
import { SeededRng } from './simulation/rng';
import type { DevelopmentLogEntry, DevelopmentSource, MatchEvent, Player, PlayerDevelopment, PlayerStatKey, Position } from './types';

export const PLAYER_STAT_KEYS: PlayerStatKey[] = ['kick', 'dribble', 'pass', 'defense', 'speed', 'power', 'control', 'stamina'];

export const playerStatLabels: Record<PlayerStatKey, string> = {
  kick: 'Tiro', dribble: 'Regate', pass: 'Pase', defense: 'Defensa',
  speed: 'Velocidad', power: 'Potencia', control: 'Control', stamina: 'Resistencia',
};

const positionWeights: Record<Position, Record<PlayerStatKey, number>> = {
  GK: { kick: 3, dribble: 3, pass: 12, defense: 30, speed: 6, power: 14, control: 22, stamina: 10 },
  DF: { kick: 3, dribble: 4, pass: 12, defense: 27, speed: 15, power: 18, control: 7, stamina: 14 },
  MF: { kick: 5, dribble: 16, pass: 21, defense: 8, speed: 13, power: 6, control: 19, stamina: 12 },
  FW: { kick: 28, dribble: 20, pass: 4, defense: 2, speed: 17, power: 9, control: 14, stamina: 6 },
};

const sourceLabels: Record<DevelopmentSource, string> = {
  match: 'Rendimiento de partido', mvp: 'MVP de la victoria', tournament: 'Victoria de torneo', spirit: 'Ritual de Espíritus',
};

export const SPIRIT_COST = 3;

export type MatchDevelopmentOutcome = 'win' | 'draw' | 'loss';
export type DevelopmentAward = { playerId: string; stat: PlayerStatKey; source: DevelopmentSource; matchSeed?: number };

export function emptyDevelopment(): PlayerDevelopment {
  return { boosts: {}, appearances: 0, wins: 0, draws: 0, losses: 0, mvps: 0, spiritSacrifices: 0, tournamentWins: 0 };
}

export function normaliseDevelopment(value: unknown): PlayerDevelopment {
  const source = value && typeof value === 'object' ? value as Partial<PlayerDevelopment> : {};
  const rawBoosts = source.boosts && typeof source.boosts === 'object'
    ? source.boosts as Record<string, unknown>
    : {};
  const boosts = Object.fromEntries(PLAYER_STAT_KEYS.flatMap((stat) => {
    // `physical` was a short-lived engine-only alias for the catalogue's
    // `power` attribute. Preserve old career progress without recreating the
    // phantom statistic on developed player profiles.
    const amount = Number(rawBoosts[stat] ?? (stat === 'power' ? rawBoosts.physical : undefined));
    return Number.isFinite(amount) && amount > 0 ? [[stat, Math.floor(amount)]] : [];
  })) as Partial<Record<PlayerStatKey, number>>;
  const count = (item: unknown) => Math.max(0, Math.floor(Number(item) || 0));
  return {
    boosts,
    appearances: count(source.appearances), wins: count(source.wins), draws: count(source.draws), losses: count(source.losses),
    mvps: count(source.mvps), spiritSacrifices: count(source.spiritSacrifices), tournamentWins: count(source.tournamentWins),
  };
}

export function resolveDevelopedPlayer(player: Player, development?: PlayerDevelopment): Player {
  if (!development) return player;
  const stats = { ...player.stats };
  PLAYER_STAT_KEYS.forEach((stat) => {
    const base = Number(player.stats[stat] ?? 0);
    stats[stat] = Math.max(0, base + (development.boosts[stat] ?? 0));
  });
  return { ...player, stats };
}

export function developmentTotal(development?: PlayerDevelopment) {
  return development ? Object.values(development.boosts).reduce((total, amount) => total + (amount ?? 0), 0) : 0;
}

export function sourceLabel(source: DevelopmentSource) {
  return sourceLabels[source];
}

/** Rolls match growth from the deterministic match seed. There is no seasonal
 * quota or hard stat ceiling: performers can keep becoming club legends. */
export function rollMatchDevelopment(players: Player[], events: MatchEvent[], side: 0 | 1, outcome: MatchDevelopmentOutcome, seed: number, tournament = false): { awards: DevelopmentAward[]; mvpId?: string } {
  if (!players.length) return { awards: [] };
  const rng = new SeededRng((seed ^ (side ? 0x7f4a7c15 : 0x9e3779b9) ^ (tournament ? 0x51a71 : 0)) >>> 0);
  const performance = new Map(players.map((player) => [player.id, 0]));
  events.filter((event) => event.team === side).forEach((event) => {
    const current = performance.get(event.actorId);
    if (current === undefined) return;
    const value = event.type === 'GOAL' ? 7 : event.type === 'SAVE' ? 3.4 : event.type === 'SHOT' ? 1.15
      : event.type === 'TACKLE' ? 1.1 : event.type === 'TECHNIQUE' ? 1 : event.type === 'DRIBBLE' ? .8
        : event.type === 'PASS' ? .08 : 0;
    performance.set(event.actorId, current + value);
  });

  const ranked = [...players].sort((left, right) => (performance.get(right.id) ?? 0) - (performance.get(left.id) ?? 0) || left.id.localeCompare(right.id));
  const mvpId = ranked[0]?.id;
  const baseChance = tournament ? (outcome === 'win' ? .14 : 0) : outcome === 'win' ? .28 : outcome === 'draw' ? .14 : .07;
  const awards: DevelopmentAward[] = [];
  players.forEach((player) => {
    const score = performance.get(player.id) ?? 0;
    const multiplier = Math.max(.5, Math.min(1.7, .5 + score / 4));
    if (hasGrowableStat(player) && rng.next() < baseChance * multiplier) awards.push({ playerId: player.id, stat: pickGrowthStat(player, rng), source: tournament ? 'tournament' : 'match', matchSeed: seed });
  });
  // There is no annual allocation: repeated decisive wins should be able to
  // turn a genuine difference-maker into a club star.
  if (!tournament && outcome === 'win' && mvpId) {
    const mvp = players.find((player) => player.id === mvpId)!;
    if (hasGrowableStat(mvp)) awards.push({ playerId: mvpId, stat: pickGrowthStat(mvp, rng), source: 'mvp', matchSeed: seed });
  }
  return { awards, mvpId };
}

export function applyDevelopmentAwards(records: Record<string, PlayerDevelopment>, awards: DevelopmentAward[], playerForId: (id: string) => Player | undefined, participants: string[] = [], outcome?: MatchDevelopmentOutcome, mvpId?: string, tournament = false): Record<string, PlayerDevelopment> {
  const next = { ...records };
  awards.forEach((award) => {
    const player = playerForId(award.playerId);
    if (!player) return;
    const record = normaliseDevelopment(next[award.playerId]);
    next[award.playerId] = { ...record, boosts: { ...record.boosts, [award.stat]: (record.boosts[award.stat] ?? 0) + 1 } };
  });
  if (!outcome) return next;
  [...new Set(participants)].forEach((id) => {
    if (!playerForId(id)) return;
    const record = normaliseDevelopment(next[id]);
    next[id] = {
      ...record,
      appearances: record.appearances + 1,
      wins: record.wins + (outcome === 'win' ? 1 : 0),
      draws: record.draws + (outcome === 'draw' ? 1 : 0),
      losses: record.losses + (outcome === 'loss' ? 1 : 0),
      // A MVP is a reward for leading a league victory. Background draws and
      // defeats can still have a best performer for presentation purposes,
      // but they must not inflate the persistent MVP record.
      mvps: record.mvps + (!tournament && outcome === 'win' && id === mvpId ? 1 : 0),
      tournamentWins: record.tournamentWins + (tournament && outcome === 'win' ? 1 : 0),
    };
  });
  return next;
}

export function createDevelopmentLog(awards: DevelopmentAward[], season: number, createdAt = new Date().toISOString()): DevelopmentLogEntry[] {
  return awards.map((award, index) => ({
    id: `${season}:${award.matchSeed ?? 'spirit'}:${award.source}:${award.playerId}:${award.stat}:${index}`,
    playerId: award.playerId,
    stat: award.stat,
    source: award.source,
    season,
    matchSeed: award.matchSeed,
    createdAt,
  }));
}

function pickGrowthStat(player: Player, rng: SeededRng): PlayerStatKey {
  const weights = { ...positionWeights[player.position] };
  const topStats = [...PLAYER_STAT_KEYS].sort((left, right) => Number(player.stats[right] ?? 0) - Number(player.stats[left] ?? 0)).slice(0, 2);
  topStats.forEach((stat) => { weights[stat] += 10; });
  player.techniques.forEach((id) => {
    const technique = techniqueById.get(id);
    if (!technique) return;
    if (technique.type === 'SHOT') { weights.kick += 7; weights.control += 3; }
    if (technique.type === 'DRIBBLE') { weights.dribble += 7; weights.speed += 3; }
    if (technique.type === 'PASS') { weights.pass += 7; weights.control += 3; }
    if (technique.type === 'BLOCK' || technique.type === 'SAVE') { weights.defense += 7; weights.power += 3; }
  });
  const values = PLAYER_STAT_KEYS.map((stat) => {
    const value = Number(player.stats[stat] ?? 0);
    return [stat, Number.isFinite(value) ? weights[stat] : 0] as const;
  });
  const total = values.reduce((sum, [, weight]) => sum + weight, 0);
  if (!total) return PLAYER_STAT_KEYS[0];
  let roll = rng.next() * total;
  for (const [stat, weight] of values) {
    roll -= weight;
    if (roll <= 0) return stat;
  }
  return values.at(-1)![0];
}

function hasGrowableStat(player: Player) {
  return PLAYER_STAT_KEYS.some((stat) => Number.isFinite(Number(player.stats[stat])));
}
